/* eslint-disable camelcase */
import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Material UI
import Button from '@material-ui/core/Button';
import CancelIcon from '@material-ui/icons/Cancel';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import EditIcon from '@material-ui/icons/Edit';
import ReplyAllIcon from '@material-ui/icons/ReplyAll';
import Tooltip from '@material-ui/core/Tooltip';

import BlessImageButton from 'components/BlessImageButton';
import ConfirmDeletionDialog from 'components/ConfirmDeletionDialog';

// props
import CommentProps from 'components/Comments/Comment/CommentProps';

// queries
import { BLESS_REVIEW_IMAGE, MODERATE_COMMENT, UNBLESS_REVIEW_IMAGE } from '../CommentQueries';

const DEFAULT_DIALOG_TITLE = "The review's image will be set as the recipe image";
const DEFAULT_DIALOG_PROCEED_TEXT = 'Proceed';

const UNBLESS_DIALOG_TITLE = "Image will no longer be recipe's image";
const UNBLESSED_DIALOG_PROCEED_TEXT = 'Remove image';

function ModeratorActions(props) {
  const { comment, onRespondClick, onEditClick, hasPrimaryImage } = props;
  const { id, moderation_status, content_id, type } = comment;

  const [confirmBlessingDialogTitle, setConfirmBlessingDialogTitle] = useState(
    DEFAULT_DIALOG_TITLE,
  );
  const [confirmBlessingDialogProceedText, setConfirmBlessingDialogProceedText] = useState(
    DEFAULT_DIALOG_PROCEED_TEXT,
  );
  const [thereIsADupBlessingError, setThereIsADupBlessingError] = useState(false);

  const [confirmBlessingDialogOpen, setConfirmBlessingDialogOpen] = useState(false);

  const [moderateComment] = useMutation(MODERATE_COMMENT);
  const [unBlessCommentsImage, { loading: isUnBlessingLoading }] = useMutation(
    UNBLESS_REVIEW_IMAGE,
    {
      onCompleted() {
        setConfirmBlessingDialogTitle(DEFAULT_DIALOG_TITLE);
        setConfirmBlessingDialogProceedText(DEFAULT_DIALOG_PROCEED_TEXT);
        toast.success("Image is no longer recipe's image");
      },
      onError(blessingError) {
        toast.error(blessingError.message);
      },
      refetchQueries: ['GetAllRecipeComments', 'GetRecipe'],
    },
  );
  const [blessCommentsImage, { loading: isBlessingLoading }] = useMutation(BLESS_REVIEW_IMAGE, {
    onCompleted() {
      setThereIsADupBlessingError(false);
      setConfirmBlessingDialogTitle(DEFAULT_DIALOG_TITLE);
      setConfirmBlessingDialogProceedText(DEFAULT_DIALOG_PROCEED_TEXT);
      toast.success('Image set as recipe image!');
    },
    onError(blessingError) {
      if (
        blessingError.graphQLErrors[0] &&
        blessingError.graphQLErrors[0].extensions.code === 'ALREADY_HAS_BLESSING'
      ) {
        setThereIsADupBlessingError(true);
        setConfirmBlessingDialogTitle('This recipe already has a blessed review image.');
        setConfirmBlessingDialogProceedText('Replace image?');
        setConfirmBlessingDialogOpen(true);
      } else {
        setThereIsADupBlessingError(false);
        toast.error(blessingError.message);
      }
    },
    refetchQueries: ['GetAllRecipeComments', 'GetRecipe'],
  });

  const saveModerationStatus = newStatus =>
    moderateComment({
      variables: {
        comment_id: id,
        content_id,
        moderation_status: newStatus,
      },
      optimisticResponse: {
        RecipeComment: {
          __typename: 'Comment',
          ...comment,
          moderation_status: newStatus,
        },
        RecipeCommentCounts: null,
      },
    });

  const handleApprove = event => {
    event.stopPropagation();
    const newStatus = moderation_status === 'approved' ? 'pending' : 'approved';
    saveModerationStatus(newStatus);
  };

  const handleReject = event => {
    event.stopPropagation();
    const newStatus = moderation_status === 'removed' ? 'pending' : 'removed';
    saveModerationStatus(newStatus);
  };

  const handleBlessImageClick = event => {
    setThereIsADupBlessingError(false);

    if (comment.comment_image_is_blessed) {
      setConfirmBlessingDialogTitle(UNBLESS_DIALOG_TITLE);
      setConfirmBlessingDialogProceedText(UNBLESSED_DIALOG_PROCEED_TEXT);
    }

    event.stopPropagation();
    setConfirmBlessingDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setConfirmBlessingDialogOpen(false);
    setThereIsADupBlessingError(false);
    setConfirmBlessingDialogTitle(DEFAULT_DIALOG_TITLE);
    setConfirmBlessingDialogProceedText(DEFAULT_DIALOG_PROCEED_TEXT);
  };

  const handleConfirmDialog = async () => {
    setConfirmBlessingDialogOpen(false);

    if (comment.comment_image_is_blessed) {
      await unBlessCommentsImage({
        variables: {
          user_id: comment.user.id,
          content_id,
          comment_id: id,
        },
      });
      return;
    }

    await blessCommentsImage({
      variables: {
        user_id: comment.user.id,
        content_id,
        force: thereIsADupBlessingError,
        comment_id: id,
      },
    });
  };

  const blessedImageTooltip = useMemo(
    () => {
      if (comment.comment_image_is_blessed) {
        return 'Do not use as recipe image';
      }

      return 'Use as recipe image';
    },
    [comment.comment_image_is_blessed],
  );

  if (moderation_status) {
    const isApproved = ['approved', 'auto_approve'].includes(moderation_status);
    const isRemoved = ['removed', 'auto_remove'].includes(moderation_status);
    const hasImage = comment.image_urls && comment.image_urls.length > 0;

    return (
      <>
        <div>
          <div className="comment-actions__moderator-tools">
            {type !== 'response' ? (
              <Tooltip title="Respond from ckbk">
                <Button onClick={onRespondClick}>
                  <ReplyAllIcon />
                </Button>
              </Tooltip>
            ) : null}

            {comment.comment_image_is_blessed || (hasImage && !hasPrimaryImage) ? (
              <Tooltip title={blessedImageTooltip}>
                <BlessImageButton
                  isBlessedImage={comment.comment_image_is_blessed}
                  isLoading={isBlessingLoading || isUnBlessingLoading}
                  onClick={handleBlessImageClick}
                />
              </Tooltip>
            ) : null}

            <Tooltip title="Edit as moderator">
              <Button onClick={onEditClick}>
                <EditIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Approve">
              <Button style={{ color: 'darkolivegreen' }} onClick={handleApprove}>
                {isApproved ? <CheckCircleIcon /> : <CheckCircleOutlineOutlinedIcon />}
              </Button>
            </Tooltip>
            <Tooltip title="Reject and hide">
              <Button style={{ color: 'indianred' }} onClick={handleReject}>
                {isRemoved ? <CancelIcon /> : <CancelOutlinedIcon />}
              </Button>
            </Tooltip>
          </div>
        </div>
        <ConfirmDeletionDialog
          title={confirmBlessingDialogTitle}
          body=""
          proceedText={confirmBlessingDialogProceedText}
          open={confirmBlessingDialogOpen}
          onClose={handleCloseDialog}
          onAgree={handleConfirmDialog}
        />
      </>
    );
  }
  return null;
}

ModeratorActions.propTypes = {
  comment: CommentProps.isRequired,
  onRespondClick: PropTypes.func,
  onEditClick: PropTypes.func,
  hasPrimaryImage: PropTypes.bool,
};

ModeratorActions.defaultProps = {
  onRespondClick: null,
  onEditClick: null,
  hasPrimaryImage: false,
};

ModeratorActions.whyDidYouRender = true;

export default React.memo(ModeratorActions);
